import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { AlertTriangle, ArrowRight, HeartPulse, RotateCcw } from "lucide-react";

import { CalculatorLayout } from "@/components/calculator/CalculatorLayout";
import { NumberInput } from "@/components/calculator/NumberInput";
import { UnitToggle } from "@/components/calculator/UnitToggle";
import { CopyResultButton } from "@/components/calculator/CopyResultButton";
import { ShareButton } from "@/components/calculator/ShareButton";
import { Button } from "@/components/ui/button";
import { useAppStore } from "@/lib/store";
import { toBengaliNumerals } from "@/lib/bdNumerals";
import { buildSeo } from "@/lib/seo";
import { cn } from "@/lib/utils";

import {
  computeAllBMR,
  selectedBMR,
  activityPreview,
  validateBMR,
  type BMRFormula,
} from "@/lib/calculators/bmr";
import type { Gender, Activity } from "@/lib/calculators/calorie";
import {
  inchesToFtIn,
  cmToIn,
  kgToLb,
  lbToKg,
  inToCm,
} from "@/lib/calculators/bmi";

type UnitSystem = "metric" | "mixed" | "imperial";
type Bilingual = { en: string; bn: string };

const STR = {
  h1: { en: "BMR Calculator", bn: "বিএমআর ক্যালকুলেটর" },
  desc: {
    en: "Calculate your Basal Metabolic Rate — the calories your body burns at complete rest. Compare Mifflin-St Jeor, Harris-Benedict and Katch-McArdle formulas.",
    bn: "আপনার বেসাল মেটাবলিক রেট হিসাব করুন — সম্পূর্ণ বিশ্রামে শরীর যে ক্যালরি পোড়ায়। Mifflin-St Jeor, Harris-Benedict ও Katch-McArdle সূত্র তুলনা করুন।",
  },
  units: { en: "Units", bn: "একক" },
  metric: { en: "Metric", bn: "মেট্রিক" },
  mixed: { en: "kg + ft/in", bn: "কেজি + ফিট/ইঞ্চি" },
  imperial: { en: "Imperial", bn: "ইম্পেরিয়াল" },
  gender: { en: "Gender", bn: "লিঙ্গ" },
  male: { en: "Male", bn: "পুরুষ" },
  female: { en: "Female", bn: "মহিলা" },
  age: { en: "Age", bn: "বয়স" },
  ageUnit: { en: "years", bn: "বছর" },
  height: { en: "Height", bn: "উচ্চতা" },
  cm: { en: "cm", bn: "সেমি" },
  ft: { en: "ft", bn: "ফিট" },
  inch: { en: "in", bn: "ইঞ্চি" },
  weight: { en: "Weight", bn: "ওজন" },
  kg: { en: "kg", bn: "কেজি" },
  lb: { en: "lb", bn: "পাউন্ড" },
  bodyFat: { en: "Body fat % (optional)", bn: "চর্বি % (ঐচ্ছিক)" },
  formula: { en: "Formula", bn: "সূত্র" },
  reset: { en: "Reset", bn: "রিসেট" },

  yourBmr: { en: "Your BMR", bn: "আপনার বিএমআর" },
  kcal: { en: "kcal/day", bn: "কিলোক্যালরি/দিন" },
  restNote: {
    en: "This is the energy your body burns at complete rest.",
    bn: "এটি সম্পূর্ণ বিশ্রামে আপনার শরীরের ক্যালরি ব্যয়।",
  },
  comparison: { en: "Formula comparison", bn: "সূত্রের তুলনা" },
  activityPreview: { en: "Activity-adjusted daily need", bn: "সক্রিয়তা-সমন্বিত দৈনিক চাহিদা" },
  goCalorie: { en: "Calculate full daily calorie need with macros", bn: "ম্যাক্রোসহ পূর্ণ দৈনিক ক্যালরি হিসাব করুন" },
  affects: { en: "What affects BMR", bn: "বিএমআর কিসে প্রভাবিত হয়" },

  err_age: { en: "Age must be 10–100.", bn: "বয়স ১০–১০০ এর মধ্যে।" },
  err_height: { en: "Height seems incorrect.", bn: "উচ্চতা সঠিক মনে হচ্ছে না।" },
  err_weight: { en: "Weight seems incorrect.", bn: "ওজন সঠিক মনে হচ্ছে না।" },
  err_bf: { en: "Body fat % must be 3–60.", bn: "চর্বি % ৩–৬০ এর মধ্যে।" },

  disclaimer: {
    en: "These values are estimates based on standard formulas. BMR can be affected by thyroid disorders, medications, and other medical conditions. Consult a doctor before making significant dietary changes.",
    bn: "এই মান সাধারণ সূত্রভিত্তিক অনুমান। থাইরয়েড সমস্যা, ওষুধ ও অন্যান্য রোগের কারণে বিএমআর প্রভাবিত হতে পারে। খাদ্যাভ্যাসে বড় পরিবর্তনের আগে ডাক্তারের পরামর্শ নিন।",
  },
};

const FORMULA_LABELS: Record<BMRFormula | "all", Bilingual> = {
  mifflin: { en: "Mifflin-St Jeor (recommended)", bn: "Mifflin-St Jeor (প্রস্তাবিত)" },
  harris: { en: "Harris-Benedict (revised)", bn: "Harris-Benedict (সংশোধিত)" },
  katch: { en: "Katch-McArdle (needs body fat %)", bn: "Katch-McArdle (চর্বি % দরকার)" },
  cunningham: { en: "Cunningham (athletes)", bn: "Cunningham (খেলোয়াড়)" },
  all: { en: "Compare all formulas", bn: "সব সূত্র তুলনা" },
};

const ACTIVITY_NAME: Record<Activity, Bilingual> = {
  sedentary: { en: "Sedentary", bn: "নিষ্ক্রিয়" },
  light: { en: "Lightly active", bn: "হালকা সক্রিয়" },
  moderate: { en: "Moderately active", bn: "মাঝারি সক্রিয়" },
  very: { en: "Very active", bn: "খুব সক্রিয়" },
  extra: { en: "Extra active", bn: "অতি সক্রিয়" },
};

const FORMULA_NOTE: Record<BMRFormula, Bilingual> = {
  mifflin: { en: "Most accurate (recommended)", bn: "সবচেয়ে নির্ভুল (প্রস্তাবিত)" },
  harris: { en: "Older, slightly higher", bn: "পুরনো, কিছুটা বেশি" },
  katch: { en: "Better for athletes (uses lean mass)", bn: "খেলোয়াড়দের জন্য ভালো (পেশী ভর)" },
  cunningham: { en: "Athletes — similar to Katch", bn: "খেলোয়াড় — Katch এর মতো" },
};

const FAQ: { q: Bilingual; a: Bilingual }[] = [
  {
    q: { en: "What is BMR?", bn: "বিএমআর কী?" },
    a: {
      en: "Basal Metabolic Rate (BMR) is the number of calories your body needs to perform basic life-sustaining functions — breathing, circulation, cell production — while at complete rest. It typically accounts for 60–75% of total daily energy expenditure.",
      bn: "বেসাল মেটাবলিক রেট (BMR) হলো শ্বাস-প্রশ্বাস, রক্ত সঞ্চালন, কোষ উৎপাদনের মতো মৌলিক জীবন কাজের জন্য সম্পূর্ণ বিশ্রামে শরীরের প্রয়োজনীয় ক্যালরি। এটি দৈনিক মোট শক্তি ব্যয়ের সাধারণত ৬০–৭৫%।",
    },
  },
  {
    q: { en: "How is BMR calculated?", bn: "বিএমআর কীভাবে গণনা করা হয়?" },
    a: {
      en: "The most accurate widely-used formula is Mifflin-St Jeor (1990): Male = 10×kg + 6.25×cm − 5×age + 5; Female = 10×kg + 6.25×cm − 5×age − 161.",
      bn: "সবচেয়ে নির্ভুল ও ব্যবহৃত সূত্র Mifflin-St Jeor (১৯৯০): পুরুষ = ১০×কেজি + ৬.২৫×সেমি − ৫×বয়স + ৫; নারী = ১০×কেজি + ৬.২৫×সেমি − ৫×বয়স − ১৬১।",
    },
  },
  {
    q: { en: "What is the difference between BMR and metabolic rate?", bn: "বিএমআর ও মেটাবলিক রেটের পার্থক্য কী?" },
    a: {
      en: "BMR is measured under strict conditions (post-sleep, no food, neutral temperature). Resting Metabolic Rate (RMR) is measured under less strict conditions and is usually 10% higher. Total Daily Energy Expenditure (TDEE) adds activity to BMR.",
      bn: "BMR কঠোর শর্তে মাপা হয় (ঘুম থেকে উঠে, খাবার ছাড়া, স্বাভাবিক তাপমাত্রায়)। Resting Metabolic Rate (RMR) কম কঠোর শর্তে মাপা হয় ও সাধারণত ১০% বেশি। TDEE হলো BMR + সক্রিয়তা।",
    },
  },
  {
    q: { en: "How can I increase my BMR?", bn: "বিএমআর কীভাবে বাড়ানো যায়?" },
    a: {
      en: "Build muscle through strength training (muscle burns more calories at rest), get adequate sleep (7–9 hours), stay hydrated, and avoid extreme calorie restriction which lowers BMR. There is no proven supplement or pill that meaningfully increases BMR.",
      bn: "শক্তি প্রশিক্ষণে পেশী বাড়ান (পেশী বিশ্রামেও বেশি ক্যালরি পোড়ায়), পর্যাপ্ত ঘুম নিন (৭–৯ ঘণ্টা), পানি পান করুন, এবং অতিরিক্ত ডায়েট এড়িয়ে চলুন যা বিএমআর কমায়। কোনো ওষুধ বা সাপ্লিমেন্ট প্রমাণিতভাবে বিএমআর বাড়ায় না।",
    },
  },
  {
    q: { en: "বিএমআর কী?", bn: "বিএমআর কী?" },
    a: {
      en: "বিএমআর হলো বিশ্রামে শরীরের ন্যূনতম শক্তি প্রয়োজন — যা শ্বাস-প্রশ্বাস, হৃদস্পন্দন ও কোষের কাজে ব্যয় হয়।",
      bn: "বিএমআর হলো বিশ্রামে শরীরের ন্যূনতম শক্তি প্রয়োজন — যা শ্বাস-প্রশ্বাস, হৃদস্পন্দন ও কোষের কাজে ব্যয় হয়।",
    },
  },
  {
    q: { en: "বিএমআর কীভাবে বাড়ানো যায়?", bn: "বিএমআর কীভাবে বাড়ানো যায়?" },
    a: {
      en: "নিয়মিত ভার উত্তোলন (পেশী বৃদ্ধি), পর্যাপ্ত ঘুম, প্রোটিন গ্রহণ ও পানি পান বিএমআর সামান্য বাড়াতে সাহায্য করে। অতিরিক্ত ডায়েট বিএমআর কমিয়ে দেয়।",
      bn: "নিয়মিত ভার উত্তোলন (পেশী বৃদ্ধি), পর্যাপ্ত ঘুম, প্রোটিন গ্রহণ ও পানি পান বিএমআর সামান্য বাড়াতে সাহায্য করে। অতিরিক্ত ডায়েট বিএমআর কমিয়ে দেয়।",
    },
  },
];

export const Route = createFileRoute("/bmr-calculator")({
  head: () => {
    const seo = buildSeo({
      title: "BMR Calculator — Basal Metabolic Rate (Mifflin)",
      description:
        "Free BMR (Basal Metabolic Rate) calculator. Calculate calories burned at rest using Mifflin-St Jeor, Harris-Benedict, Katch-McArdle formulas. বিএমআর ক্যালকুলেটর।",
      path: "/bmr-calculator",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "BMR Calculator", path: "/bmr-calculator" },
      ],
      faq: FAQ.map((f) => ({ q: f.q.en, a: f.a.en })),
    });
    return { meta: seo.meta, links: seo.links, scripts: seo.scripts };
  },
  component: BMRCalculatorPage,
});

function BMRCalculatorPage() {
  const lang = useAppStore((s) => s.lang);
  const tr = (k: keyof typeof STR) => (STR[k] as Bilingual)[lang];
  const num = (n: number | string) => (lang === "bn" ? toBengaliNumerals(n) : String(n));
  const fmt = (n: number) => num(n.toLocaleString("en-US"));

  const [unit, setUnit] = useState<UnitSystem>("metric");
  const [gender, setGender] = useState<Gender>("male");
  const [age, setAge] = useState("30");
  const [heightCm, setHeightCm] = useState<number | null>(170);
  const [weightKg, setWeightKg] = useState<number | null>(70);
  const [cmStr, setCmStr] = useState("170");
  const [kgStr, setKgStr] = useState("70");
  const [ft, setFt] = useState(() => String(inchesToFtIn(cmToIn(170)).ft));
  const [inch, setInch] = useState(() => String(Math.round(inchesToFtIn(cmToIn(170)).inch)));
  const [lb, setLb] = useState(() => kgToLb(70).toFixed(1));
  const [bodyFat, setBodyFat] = useState("");
  const [formula, setFormula] = useState<BMRFormula | "all">("mifflin");

  useEffect(() => {
    if (unit !== "metric") return;
    const h = parseFloat(cmStr);
    setHeightCm(Number.isFinite(h) ? h : null);
  }, [cmStr, unit]);
  useEffect(() => {
    if (unit === "imperial") return;
    const w = parseFloat(kgStr);
    setWeightKg(Number.isFinite(w) ? w : null);
  }, [kgStr, unit]);
  useEffect(() => {
    if (unit === "metric") return;
    const f = parseFloat(ft);
    const i = parseFloat(inch);
    if (Number.isFinite(f) || Number.isFinite(i)) {
      const totalIn = (Number.isFinite(f) ? f : 0) * 12 + (Number.isFinite(i) ? i : 0);
      setHeightCm(totalIn > 0 ? +inToCm(totalIn).toFixed(2) : null);
    } else setHeightCm(null);
  }, [ft, inch, unit]);
  useEffect(() => {
    if (unit !== "imperial") return;
    const p = parseFloat(lb);
    setWeightKg(Number.isFinite(p) ? +lbToKg(p).toFixed(2) : null);
  }, [lb, unit]);

  const switchUnit = (next: UnitSystem) => {
    if (next === unit) return;
    if (heightCm != null && weightKg != null) {
      if (next !== "metric") {
        const totalIn = cmToIn(heightCm);
        const { ft: f, inch: i } = inchesToFtIn(totalIn);
        setFt(String(f));
        setInch(String(Math.round(i)));
      }
      if (next === "imperial") {
        setLb(kgToLb(weightKg).toFixed(1));
      }
      if (next === "metric") {
        setCmStr(String(Math.round(heightCm)));
      }
      if (next !== "imperial") {
        setKgStr(weightKg.toFixed(1).replace(/\.0$/, ""));
      }
    }
    setUnit(next);
  };

  const ageNum = age.trim() === "" ? NaN : Number(age);
  const bfNum = bodyFat.trim() === "" ? undefined : Number(bodyFat);

  const validation = useMemo(() => {
    const checkFormula = formula === "all" ? "mifflin" : formula;
    return validateBMR({
      gender, age: ageNum,
      heightCm: heightCm ?? undefined,
      weightKg: weightKg ?? undefined,
      bodyFatPct: bfNum,
      formula: checkFormula,
    });
  }, [gender, ageNum, heightCm, weightKg, bfNum, formula]);

  const breakdown = useMemo(() => {
    if (!validation.ok || heightCm == null || weightKg == null) return null;
    return computeAllBMR({
      gender, age: ageNum, heightCm, weightKg,
      bodyFatPct: bfNum,
    });
  }, [validation.ok, gender, ageNum, heightCm, weightKg, bfNum]);

  const primaryBmr = useMemo(() => {
    if (!breakdown) return null;
    if (formula === "all") return breakdown.mifflin;
    return selectedBMR(breakdown, formula);
  }, [breakdown, formula]);

  const errKey = !validation.ok ? validation.error : null;
  const ageError = errKey === "age_invalid" ? tr("err_age") : null;
  const heightError = errKey === "height_invalid" ? tr("err_height") : null;
  const weightError = errKey === "weight_invalid" ? tr("err_weight") : null;
  const bfError = errKey === "bf_invalid" ? tr("err_bf") : null;

  const reset = () => {
    setUnit("metric"); setGender("male"); setAge("30");
    setCmStr("170"); setKgStr("70");
    setFt("5"); setInch("7"); setLb(kgToLb(70).toFixed(1));
    setHeightCm(170); setWeightKg(70);
    setBodyFat(""); setFormula("mifflin");
  };

  const shareLine = primaryBmr != null ? `${tr("yourBmr")}: ${fmt(primaryBmr)} ${tr("kcal")}` : "";
  const tdee = primaryBmr != null ? activityPreview(primaryBmr) : null;

  return (
    <CalculatorLayout
      slug="bmr-calculator"
      title={tr("h1")}
      description={tr("desc")}
      iconName="HeartPulse"
      faq={FAQ.map((f) => ({ q: f.q[lang], a: f.a[lang] }))}
    >
      <div className="flex flex-wrap items-end justify-between gap-4">
        <UnitToggle<UnitSystem>
          label={tr("units")}
          value={unit}
          onChange={switchUnit}
          options={[
            { value: "metric", label: tr("metric") },
            { value: "mixed", label: tr("mixed") },
            { value: "imperial", label: tr("imperial") },
          ]}
        />
      </div>

      <div className="mt-5 grid gap-5 md:grid-cols-2">
        <div className="space-y-1.5">
          <label className={cn("block text-sm font-medium", lang === "bn" && "font-bn")}>{tr("gender")}</label>
          <div className="inline-flex rounded-lg border border-border bg-muted p-0.5">
            {(["male", "female"] as Gender[]).map((g) => {
              const active = g === gender;
              return (
                <button
                  key={g}
                  type="button"
                  onClick={() => setGender(g)}
                  className={cn(
                    "rounded-md px-3 py-1.5 text-sm font-medium transition",
                    active ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground",
                    lang === "bn" && "font-bn",
                  )}
                >
                  {g === "male" ? tr("male") : tr("female")}
                </button>
              );
            })}
          </div>
        </div>

        <NumberInput label={tr("age")} value={age} onChange={setAge} unit={tr("ageUnit")} error={ageError} min={10} max={100} />

        {unit === "metric" ? (
          <NumberInput label={tr("height")} value={cmStr} onChange={setCmStr} unit={tr("cm")} error={heightError} placeholder="170" />
        ) : (
          <div className="space-y-1.5">
            <label className={cn("block text-sm font-medium", lang === "bn" && "font-bn")}>{tr("height")}</label>
            <div className="grid grid-cols-2 gap-3">
              <NumberInput label="" value={ft} onChange={setFt} unit={tr("ft")} />
              <NumberInput label="" value={inch} onChange={setInch} unit={tr("inch")} />
            </div>
            {heightError && <p className={cn("text-xs text-destructive", lang === "bn" && "font-bn")}>{heightError}</p>}
          </div>
        )}

        {unit === "imperial" ? (
          <NumberInput label={tr("weight")} value={lb} onChange={setLb} unit={tr("lb")} error={weightError} />
        ) : (
          <NumberInput label={tr("weight")} value={kgStr} onChange={setKgStr} unit={tr("kg")} error={weightError} placeholder="70" />
        )}

        <NumberInput label={tr("bodyFat")} value={bodyFat} onChange={setBodyFat} unit="%" error={bfError} min={3} max={60} placeholder="—" />

        <div className="space-y-1.5">
          <label className={cn("block text-sm font-medium", lang === "bn" && "font-bn")}>{tr("formula")}</label>
          <select
            value={formula}
            onChange={(e) => setFormula(e.target.value as BMRFormula | "all")}
            className={cn(
              "w-full rounded-lg border border-border bg-muted px-3 py-2.5 text-sm outline-none focus:bg-background focus:ring-2 focus:ring-accent",
              lang === "bn" && "font-bn",
            )}
          >
            {(["mifflin", "harris", "katch", "cunningham", "all"] as const).map((f) => (
              <option key={f} value={f}>{FORMULA_LABELS[f][lang]}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="mt-5 flex justify-end">
        <Button variant="outline" onClick={reset} className="gap-2">
          <RotateCcw size={14} />
          {tr("reset")}
        </Button>
      </div>

      {primaryBmr != null && breakdown && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="mt-8 space-y-6"
        >
          <div className="rounded-xl border border-accent/30 bg-accent/5 p-5">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className={cn("text-xs font-semibold uppercase tracking-wide text-accent", lang === "bn" && "font-bn")}>
                  {tr("yourBmr")}
                </p>
                <p className="mt-1 flex items-baseline gap-2 text-4xl font-semibold tabular-nums text-foreground md:text-5xl">
                  <HeartPulse size={28} className="text-accent" />
                  {fmt(primaryBmr)}
                  <span className={cn("text-sm font-normal text-muted-foreground", lang === "bn" && "font-bn")}>{tr("kcal")}</span>
                </p>
                <p className={cn("mt-2 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>{tr("restNote")}</p>
              </div>
              <CopyResultButton text={shareLine} />
            </div>
          </div>

          {/* Comparison */}
          {formula === "all" && (
            <div>
              <h3 className={cn("mb-3 text-sm font-bold", lang === "bn" && "font-bn")}>{tr("comparison")}</h3>
              <div className="overflow-hidden rounded-xl border border-border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/60 text-xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className={cn("px-3 py-2 text-left", lang === "bn" && "font-bn")}>{tr("formula")}</th>
                      <th className="px-3 py-2 text-right">{tr("kcal")}</th>
                      <th className={cn("px-3 py-2 text-left", lang === "bn" && "font-bn")}>—</th>
                    </tr>
                  </thead>
                  <tbody>
                    <CmpRow lang={lang} name={FORMULA_LABELS.mifflin[lang]} v={breakdown.mifflin} note={FORMULA_NOTE.mifflin[lang]} fmt={fmt} />
                    <CmpRow lang={lang} name={FORMULA_LABELS.harris[lang]} v={breakdown.harris} note={FORMULA_NOTE.harris[lang]} fmt={fmt} />
                    {breakdown.katch != null && (
                      <CmpRow lang={lang} name={FORMULA_LABELS.katch[lang]} v={breakdown.katch} note={FORMULA_NOTE.katch[lang]} fmt={fmt} />
                    )}
                    {breakdown.cunningham != null && (
                      <CmpRow lang={lang} name={FORMULA_LABELS.cunningham[lang]} v={breakdown.cunningham} note={FORMULA_NOTE.cunningham[lang]} fmt={fmt} />
                    )}
                  </tbody>
                </table>
              </div>
              {(breakdown.katch == null || breakdown.cunningham == null) && (
                <p className={cn("mt-2 text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
                  {lang === "bn"
                    ? "Katch-McArdle ও Cunningham দেখতে চর্বি % দিন।"
                    : "Enter body fat % to also see Katch-McArdle and Cunningham."}
                </p>
              )}
            </div>
          )}

          {/* Activity preview */}
          {tdee && (
            <div>
              <h3 className={cn("mb-3 text-sm font-bold", lang === "bn" && "font-bn")}>{tr("activityPreview")}</h3>
              <div className="grid gap-2 sm:grid-cols-2 md:grid-cols-5">
                {(Object.keys(tdee) as Activity[]).map((a) => (
                  <div key={a} className="rounded-lg border border-border bg-card p-3">
                    <p className={cn("text-xs text-muted-foreground", lang === "bn" && "font-bn")}>{ACTIVITY_NAME[a][lang]}</p>
                    <p className="mt-1 text-base font-semibold tabular-nums">{fmt(tdee[a])}</p>
                    <p className={cn("text-[10px] text-muted-foreground", lang === "bn" && "font-bn")}>{tr("kcal")}</p>
                  </div>
                ))}
              </div>
              <Link
                to="/calorie-calculator"
                className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-accent hover:underline"
              >
                <span className={lang === "bn" ? "font-bn" : ""}>{tr("goCalorie")}</span>
                <ArrowRight size={14} />
              </Link>
            </div>
          )}

          {/* What affects BMR */}
          <div className="rounded-xl border border-border bg-card p-5">
            <h3 className={cn("mb-3 text-sm font-bold", lang === "bn" && "font-bn")}>{tr("affects")}</h3>
            <ul className={cn("grid gap-2 text-sm text-muted-foreground md:grid-cols-2", lang === "bn" && "font-bn")}>
              <li>• {lang === "bn" ? "বয়স — ২০ বছরের পর প্রতি দশকে ~২% কমে" : "Age — drops ~2% per decade after 20"}</li>
              <li>• {lang === "bn" ? "পেশী ভর — বেশি পেশী = বেশি বিএমআর" : "Muscle mass — more muscle = higher BMR"}</li>
              <li>• {lang === "bn" ? "জিনগত প্রভাব" : "Genetics"}</li>
              <li>• {lang === "bn" ? "হরমোন (থাইরয়েড)" : "Hormones (thyroid)"}</li>
              <li>• {lang === "bn" ? "তাপমাত্রা / আবহাওয়া" : "Climate / temperature"}</li>
              <li>• {lang === "bn" ? "ঘুম ও পুষ্টি" : "Sleep & nutrition"}</li>
            </ul>
          </div>

          {/* Disclaimer */}
          <div className={cn(
            "flex items-start gap-3 rounded-xl border border-yellow-400/30 bg-yellow-400/5 p-4 text-xs text-foreground/80",
            lang === "bn" && "font-bn",
          )}>
            <AlertTriangle size={16} className="mt-0.5 shrink-0 text-yellow-500" />
            <p>{tr("disclaimer")}</p>
          </div>

          <div className="flex justify-end">
            <ShareButton title={shareLine} />
          </div>
        </motion.div>
      )}

      <article className="mt-12 space-y-10">
        <Section title={lang === "bn" ? "বিএমআর কী?" : "What is BMR?"} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "bn"
              ? "বেসাল মেটাবলিক রেট (BMR) হলো সম্পূর্ণ বিশ্রামে আপনার শরীর প্রতিদিন কত ক্যালরি পোড়ায় তার পরিমাপ — শ্বাস-প্রশ্বাস, রক্ত সঞ্চালন, কোষ মেরামত ও মস্তিষ্কের কাজে ব্যয় হয়। এটি দৈনিক মোট ক্যালরি ব্যয়ের প্রায় ৬০–৭৫% দখল করে।"
              : "Basal Metabolic Rate (BMR) is how many calories your body burns per day at complete rest — for breathing, circulation, cell repair and brain activity. It represents about 60–75% of total daily energy expenditure."}
          </p>
        </Section>

        <Section title={lang === "bn" ? "BMR বনাম TDEE" : "BMR vs TDEE"} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "bn"
              ? "BMR শুধু বিশ্রামের ক্যালরি পরিমাপ করে। TDEE (টোটাল ডেইলি এনার্জি এক্সপেন্ডিচার) এর সঙ্গে দৈনন্দিন কাজ ও ব্যায়ামে খরচ হওয়া ক্যালরি যোগ করে — এটিই আপনার প্রকৃত দৈনিক চাহিদা।"
              : "BMR measures resting calories only. TDEE (Total Daily Energy Expenditure) adds calories burned through daily activity and exercise — this is your actual daily need."}
          </p>
        </Section>

        <Section title={lang === "bn" ? "Mifflin-St Jeor সূত্র" : "Mifflin-St Jeor formula"} lang={lang}>
          <ul className={cn("space-y-1 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            <li>{lang === "bn" ? "পুরুষ:" : "Male:"} BMR = (10 × kg) + (6.25 × cm) − (5 × age) + 5</li>
            <li>{lang === "bn" ? "নারী:" : "Female:"} BMR = (10 × kg) + (6.25 × cm) − (5 × age) − 161</li>
            <li className="text-foreground">{lang === "bn" ? "১৯৯০ সালে প্রকাশিত — বর্তমানে সবচেয়ে নির্ভুল হিসেবে স্বীকৃত।" : "Published in 1990 — currently the most accurate widely-used formula."}</li>
          </ul>
        </Section>

        <Section title={lang === "bn" ? "Harris-Benedict (১৯১৯ / সংশোধিত ১৯৮৪)" : "Harris-Benedict (1919 / revised 1984)"} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "bn"
              ? "১৯১৯ সালে প্রকাশিত মূল সূত্র, ১৯৮৪ সালে সংশোধিত। সাধারণত Mifflin-St Jeor এর চেয়ে কিছুটা বেশি BMR দেয়। আমরা সংশোধিত (১৯৮৪) সংস্করণ ব্যবহার করি।"
              : "Originally published in 1919 and revised in 1984. Typically returns a slightly higher BMR than Mifflin-St Jeor. We use the revised (1984) version."}
          </p>
        </Section>

        <Section title={lang === "bn" ? "Katch-McArdle ও Cunningham" : "Katch-McArdle & Cunningham"} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "bn"
              ? "এই সূত্রগুলো পেশী ভরের (lean body mass) উপর ভিত্তি করে — যাদের চর্বি % জানা আছে (যেমন খেলোয়াড়), তাদের জন্য বেশি নির্ভুল। চর্বি % না জানলে DEXA, BIA বা ক্যালিপার পদ্ধতিতে মাপাতে হবে।"
              : "These formulas are based on lean body mass — more accurate for those who know their body fat % (e.g. athletes). If you don't know it, measure via DEXA, BIA scale, or skinfold calipers."}
          </p>
        </Section>

        <Section title={lang === "bn" ? "কীভাবে BMR বাড়ানো যায়" : "How to increase BMR"} lang={lang}>
          <ul className={cn("list-disc space-y-1 pl-5 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            <li>{lang === "bn" ? "শক্তি প্রশিক্ষণ — পেশী ভর বাড়ান" : "Strength training — build muscle mass"}</li>
            <li>{lang === "bn" ? "৭–৯ ঘণ্টা পর্যাপ্ত ঘুম" : "7–9 hours of quality sleep"}</li>
            <li>{lang === "bn" ? "পর্যাপ্ত পানি ও প্রোটিন" : "Adequate hydration and protein"}</li>
            <li>{lang === "bn" ? "অতিরিক্ত ক্যালরি ঘাটতি এড়িয়ে চলুন — দীর্ঘ ডায়েট BMR কমিয়ে দেয়" : "Avoid extreme calorie deficits — they lower BMR over time"}</li>
          </ul>
          <p className={cn("mt-2 text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "bn"
              ? "কোনো ওষুধ, সাপ্লিমেন্ট বা চা প্রমাণিতভাবে BMR বাড়ায় না।"
              : "No pill, supplement or tea is proven to meaningfully increase BMR."}
          </p>
        </Section>

        <Section title={lang === "bn" ? "দাবিত্যাগ" : "Disclaimer"} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>{tr("disclaimer")}</p>
        </Section>
      </article>
    </CalculatorLayout>
  );
}

function CmpRow({
  lang, name, v, note, fmt,
}: { lang: "en" | "bn"; name: string; v: number; note: string; fmt: (n: number) => string }) {
  return (
    <tr className="border-t border-border">
      <td className={cn("px-3 py-2", lang === "bn" && "font-bn")}>{name}</td>
      <td className="px-3 py-2 text-right tabular-nums font-semibold">{fmt(v)}</td>
      <td className={cn("px-3 py-2 text-xs text-muted-foreground", lang === "bn" && "font-bn")}>{note}</td>
    </tr>
  );
}

function Section({ title, children, lang }: { title: string; children: React.ReactNode; lang: "en" | "bn" }) {
  return (
    <section>
      <h2 className={cn("mb-3 text-lg font-bold tracking-tight", lang === "bn" && "font-bn")}>{title}</h2>
      {children}
    </section>
  );
}
